import { useMemo } from 'react'
import { AxiosError } from 'axios'
import qs from 'qs'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { useDispatch } from '@tabeeb/store'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { ApiErrorResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import type {
  GetQualificationSortColumn,
  QualificationFilterModelDto,
  SortingOrder,
} from '@tabeeb/contracts/microservices/swagger-certificate-service'
import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'
import getErrorMessageFromResponse from '@tabeeb/modules/shared/utils/getErrorMessageFromResponse'

import { QualificationsQueryKeys } from '../queries'

export interface UseQualificationsGridProps {
  search?: string
  sorting?: { field: GetQualificationSortColumn; order: SortingOrder } | null
  filtering?: QualificationFilterModelDto[]
  pageNumber?: number
  pageSize?: number
}

const useQualifications = ({
  search,
  sorting,
  filtering = [],
  pageNumber = 0,
  pageSize = 25,
}: UseQualificationsGridProps) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { certificateServiceApi: api } = useApi()

  const qualificationsQuery = useQuery({
    queryKey: [QualificationsQueryKeys.Qualifications, search, sorting, filtering, pageNumber, pageSize],
    queryFn: () => {
      return api.qualifications.getAllWithPaginationAsync(
        {
          ...(sorting && {
            'Sort.Field': sorting.field,
            'Sort.SortOrder': sorting.order,
          }),
          FilterModels: filtering,
          PageNumber: pageNumber,
          PageSize: pageSize,
          ...(search && { Search: search }),
        },
        {
          paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
        }
      )
    },
  })

  const createMutation = useMutation({
    mutationFn: api.qualifications.createAsync,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QualificationsQueryKeys.Qualifications] })
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: api.qualifications.deleteAsync,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QualificationsQueryKeys.Qualifications] })
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    },
  })

  const restoreMutation = useMutation({
    mutationFn: api.qualifications.restoreAsync,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QualificationsQueryKeys.Qualifications] })
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    },
  })

  const loading = useMemo(
    () =>
      qualificationsQuery.isLoading ||
      qualificationsQuery.isFetching ||
      createMutation.isPending ||
      deleteMutation.isPending ||
      restoreMutation.isPending,
    [
      qualificationsQuery.isLoading,
      qualificationsQuery.isFetching,
      createMutation.isPending,
      deleteMutation.isPending,
      restoreMutation.isPending,
    ]
  )

  return useMemo(
    () => ({
      loading,
      qualifications: qualificationsQuery.data?.Qualifications ?? [],
      totalCount: qualificationsQuery.data?.TotalCount ?? 0,
      createAsync: createMutation.mutateAsync,
      deleteAsync: deleteMutation.mutateAsync,
      restoreAsync: restoreMutation.mutateAsync,
      onReload: () => qualificationsQuery.refetch(),
    }),
    [loading, qualificationsQuery, createMutation.mutateAsync, deleteMutation.mutateAsync, restoreMutation.mutateAsync]
  )
}

export default useQualifications
